import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Divider,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import menuConsumptionRecipeService from '../../../../services/menuConsumptionRecipeService';
import inventoryItemService from '../../../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import type { MenuConsumptionRecipeSizeUnit } from '../../../../types/menu';

// Phase 4.L — matches the wording on the list page and embedded MenuRecipesPanel.
const INERT_BANNER =
  'Recipes saved here can be reused across menus. They are not active for sales or stock yet.';

const SIZE_UNITS: MenuConsumptionRecipeSizeUnit[] = ['ml', 'cl', 'l', 'g', 'kg', 'unit'];

interface ComponentRow {
  inventoryItem: InventoryItemWithBalance | null;
  /** Free-text during typing; validated on save. */
  quantity: string;
  /** '' means no unit (sizeUnit omitted on save). */
  sizeUnit: '' | MenuConsumptionRecipeSizeUnit;
}

interface FormData {
  name: string;
  notes: string;
  active: boolean;
}

const emptyForm: FormData = { name: '', notes: '', active: true };

const emptyComponentRow = (): ComponentRow => ({
  inventoryItem: null,
  quantity: '',
  sizeUnit: '',
});

const MenuConsumptionRecipeForm: React.FC = () => {
  const navigate = useNavigate();
  const { recipeId } = useParams<{ recipeId?: string }>();
  const theme = useTheme();
  const { venueId } = useVenue();
  const isEdit = Boolean(recipeId);
  const backPath = `/admin/venues/${venueId}/menu-consumption-recipes`;

  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [components, setComponents] = useState<ComponentRow[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Shared debounced inventory search — feeds every row's Autocomplete options.
  const [invSearchResults, setInvSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [invSearchLoading, setInvSearchLoading] = useState(false);

  const runInvSearch = useMemo(
    () => debounce(async (q: string) => {
      if (!q.trim()) { setInvSearchResults([]); setInvSearchLoading(false); return; }
      setInvSearchLoading(true);
      try {
        const res = await inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', q);
        setInvSearchResults(res.data ?? []);
      } catch {
        setInvSearchResults([]);
      } finally {
        setInvSearchLoading(false);
      }
    }, 350),
    [venueId],
  );
  useEffect(() => () => runInvSearch.cancel(), [runInvSearch]);

  const inventoryItemLabel = (item: InventoryItemWithBalance) => {
    const name = item.displayName;
    const size = item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : null;
    return size ? `${name} · ${size}` : name;
  };

  // ── Load existing recipe in edit mode ─────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !recipeId) return;
    setLoading(true);
    menuConsumptionRecipeService.getRecipe(venueId, recipeId)
      .then(async recipe => {
        setFormData({
          name: recipe.name,
          notes: recipe.notes ?? '',
          active: recipe.active,
        });
        // Resolve each component's inventory item so the Autocomplete shows
        // a real label (otherwise we'd only have the bare ObjectId).
        const resolved: ComponentRow[] = await Promise.all((recipe.components ?? []).map(async c => {
          let invItem: InventoryItemWithBalance | null = null;
          try {
            invItem = (await inventoryItemService.getItem(venueId, c.inventoryItemId)) as unknown as InventoryItemWithBalance;
          } catch { /* non-blocking */ }
          return {
            inventoryItem: invItem,
            quantity: String(c.quantity),
            sizeUnit: (c.sizeUnit ?? '') as ComponentRow['sizeUnit'],
          };
        }));
        setComponents(resolved);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, recipeId, venueId]);

  // ── Field handlers ────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const addComponent = () => {
    setComponents(rows => [...rows, emptyComponentRow()]);
  };

  const updateComponent = (idx: number, patch: Partial<ComponentRow>) => {
    setComponents(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const removeComponent = (idx: number) => {
    setComponents(rows => rows.filter((_, i) => i !== idx));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be at most 200 characters.';
    }
    if (formData.notes && formData.notes.length > 2000) {
      newErrors.notes = 'Notes must be at most 2000 characters.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Validate components (empty array is allowed).
    for (let i = 0; i < components.length; i++) {
      const row = components[i];
      if (!row.inventoryItem) {
        setApiError(`Component ${i + 1} needs an inventory item selected.`);
        return false;
      }
      const q = parseFloat(row.quantity);
      if (!row.quantity || isNaN(q) || q <= 0) {
        setApiError(`Component ${i + 1} needs a positive quantity.`);
        return false;
      }
      if (row.sizeUnit !== '' && !SIZE_UNITS.includes(row.sizeUnit as MenuConsumptionRecipeSizeUnit)) {
        setApiError(`Component ${i + 1} has an invalid size unit.`);
        return false;
      }
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      const payloadComponents = components.map(c => ({
        inventoryItemId: c.inventoryItem!._id,
        quantity: parseFloat(c.quantity),
        ...(c.sizeUnit !== '' && { sizeUnit: c.sizeUnit as MenuConsumptionRecipeSizeUnit }),
      }));

      if (isEdit && recipeId) {
        // Replace-all components on update (backend supports this directly).
        await menuConsumptionRecipeService.updateRecipe(venueId, recipeId, {
          name: formData.name.trim(),
          notes: formData.notes.trim() || '',
          components: payloadComponents,
          active: formData.active,
        });
      } else {
        await menuConsumptionRecipeService.createRecipe(venueId, {
          name: formData.name.trim(),
          notes: formData.notes.trim() || undefined,
          components: payloadComponents,
        });
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message;
      if (code === 'MENU_CONSUMPTION_RECIPE_NAME_DUPLICATE') {
        setErrors(prev => ({ ...prev, name: 'An active recipe with this name already exists in this venue.' }));
      } else if (code === 'MENU_CONSUMPTION_RECIPE_NOT_FOUND') {
        setApiError('This recipe no longer exists. Go back and try again.');
      } else {
        setApiError(backendMessage ?? 'Failed to save the recipe. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Recipes
        </Button>
        <Alert severity="error">
          Could not load this recipe. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Persistent INERT banner — same wording as the list page. */}
      <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
        {INERT_BANNER}
      </Alert>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Recipes
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Recipe' : 'New Recipe'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Name */}
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={Boolean(errors.name)}
            helperText={errors.name ?? 'Required. Used to pick this recipe from a menu item.'}
            fullWidth
            required
            placeholder="e.g. House Margarita 50ml pour"
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />

          {/* Notes */}
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            error={Boolean(errors.notes)}
            helperText={errors.notes ?? 'Optional. Internal notes about how this recipe is used.'}
            fullWidth
            multiline
            rows={3}
            placeholder="Internal notes (optional)"
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />

          {/* Active toggle — edit mode only */}
          {isEdit && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={e => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                />
              }
              label="Active"
            />
          )}

          {/* Components editor */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Components</Typography>
                <Typography variant="caption" color="text.secondary">
                  Inventory items that would be depleted when this recipe is served. Storage-only for now.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addComponent}
                sx={{ textTransform: 'none' }}
              >
                Add component
              </Button>
            </Box>

            {components.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No components yet. An empty recipe is allowed — add components when you know them.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {components.map((row, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 2, minWidth: 240 }}>
                        <Autocomplete
                          size="small"
                          options={invSearchResults}
                          value={row.inventoryItem}
                          loading={invSearchLoading}
                          filterOptions={x => x}
                          getOptionLabel={inventoryItemLabel}
                          isOptionEqualToValue={(opt, val) => opt._id === val._id}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input') runInvSearch(value);
                            if (reason === 'clear') updateComponent(idx, { inventoryItem: null });
                          }}
                          onChange={(_, val) => updateComponent(idx, { inventoryItem: val })}
                          renderOption={(props, opt) => (
                            <Box component="li" {...props} key={opt._id}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>{inventoryItemLabel(opt)}</Typography>
                                {opt.balance != null && (
                                  <Typography variant="caption" color="text.secondary">{opt.balance.totalQuantity} in stock</Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Inventory item"
                              placeholder="Search inventory…"
                              required
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {invSearchLoading && <CircularProgress size={14} />}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                },
                              }}
                            />
                          )}
                        />
                      </Box>
                      <TextField
                        size="small"
                        label="Quantity"
                        value={row.quantity}
                        onChange={e => updateComponent(idx, { quantity: e.target.value })}
                        type="number"
                        sx={{ width: 130 }}
                        required
                        slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                      />
                      <FormControl size="small" sx={{ width: 130 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={row.sizeUnit}
                          label="Unit"
                          onChange={e => updateComponent(idx, { sizeUnit: e.target.value as ComponentRow['sizeUnit'] })}
                        >
                          <MenuItem value=""><em>none</em></MenuItem>
                          {SIZE_UNITS.map(u => (
                            <MenuItem key={u} value={u}>{u}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeComponent(idx)}
                        aria-label="Remove component"
                        sx={{ alignSelf: 'center' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Recipe'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MenuConsumptionRecipeForm;
